// =========================================================================
// Cobranza Hierarchy — drill-down "Vendí · Frecuencia · Pago saneado"
// =========================================================================
//
// Toma el `distribution[]` enriquecido que devuelve el endpoint
// /api/insurance/dashboard/status-distribution y produce la jerarquía
// comercial que alimenta la card "Cobranza y Comisión" del tab Analítica
// Avanzada:
//
//   Vendí (ISSUED + PR + COMPLETED)
//   ├─ Anual / Mensual
//   ├─ Solo emitidas (ISSUED, pago pendiente)
//   │   └─ Anual / Mensual
//   ├─ Pagadas saneadas (PR + COMP con notif OK)
//   │   └─ Anual / Mensual
//   └─ Pagadas con error notif (PR + COMP con notif true)
//       └─ Anual / Mensual
//
// Identidad invariante:
//   Solo emitidas + Pagadas saneadas + Pagadas con error ≡ Vendí
//
// Los datos por frecuencia de PR y COMP vienen del cross 3D
// `cross_frequency_notification` del breakdown. ISSUED usa su `by_frequency`
// porque no tiene pago.

import { StatusDistribution } from '../models/seguros.interface';

export interface FrequencySplit {
  total: number;
  ANUAL: number;
  MENSUAL: number;
}

export interface CobranzaHierarchy {
  vendi: FrequencySplit;
  soloEmitidas: FrequencySplit;
  pagadasSaneadas: FrequencySplit;
  pagadasConError: FrequencySplit;
}

const emptySplit = (): FrequencySplit => ({ total: 0, ANUAL: 0, MENSUAL: 0 });

const addToSplit = (split: FrequencySplit, frequency: string | undefined, count: number) => {
  split.total += count;
  if (frequency === 'ANUAL') split.ANUAL += count;
  else if (frequency === 'MENSUAL') split.MENSUAL += count;
  // SEMESTRAL/TRIMESTRAL no aparecen hoy — quedan solo en el total.
};

export function deriveCobranzaHierarchy(distribution: StatusDistribution[]): CobranzaHierarchy {
  const soloEmitidas = emptySplit();
  const pagadasSaneadas = emptySplit();
  const pagadasConError = emptySplit();

  for (const item of distribution || []) {
    if (item.status === 'ISSUED') {
      // ISSUED no tiene pago — todo va a "Solo emitidas". Lo desglosamos
      // por frecuencia usando by_frequency.
      const byFreq = item.breakdown?.by_frequency || [];
      if (byFreq.length === 0) {
        // Sin breakdown — al menos el total.
        soloEmitidas.total += item.count;
      } else {
        for (const bk of byFreq) {
          addToSplit(soloEmitidas, bk.key, bk.count);
        }
      }
      continue;
    }
    if (item.status === 'PAYMENT_RECORDED' || item.status === 'ISSUED_PAID' || item.status === 'COMPLETED') {
      // PR, ISSUED_PAID (refactor SC/RCV, Plan 3) y COMP: usar el cross 3D si está disponible.
      const cross = item.breakdown?.cross_frequency_notification;
      if (cross && cross.length > 0) {
        for (const c of cross) {
          if (c.notification === 'notified_ok') {
            addToSplit(pagadasSaneadas, c.frequency, c.count);
          } else if (c.notification === 'notification_error') {
            addToSplit(pagadasConError, c.frequency, c.count);
          }
        }
      } else {
        // Fallback sin cross: suma al total saneado (no podemos saber el split).
        pagadasSaneadas.total += item.count;
      }
    }
  }

  // Vendí = suma de los 3 subgrupos (identidad invariante).
  const vendi: FrequencySplit = {
    total: soloEmitidas.total + pagadasSaneadas.total + pagadasConError.total,
    ANUAL: soloEmitidas.ANUAL + pagadasSaneadas.ANUAL + pagadasConError.ANUAL,
    MENSUAL: soloEmitidas.MENSUAL + pagadasSaneadas.MENSUAL + pagadasConError.MENSUAL
  };

  return { vendi, soloEmitidas, pagadasSaneadas, pagadasConError };
}
